import { useState } from 'react';
import useGetProductListRetailerAssignmentsQuery from '@/api/queries/useGetProductListRetailerAssignmentsQuery';
import useGetRetailersByIdsQuery from '@/api/queries/useGetRetailersByIdsQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { Retailer } from '@/models/Retailer';
import { isStatusActive } from '@/models/Status';
import ProductListEmptyState from '@/modules/product/components/ProductListEmptyState';
import ProductListErrorState from '@/modules/product/components/ProductListErrorState';
import { TrashIcon } from '@heroicons/react/24/outline';

interface ProductListRetailerTableProps {
  assignmentQuery: ReturnType<typeof useGetProductListRetailerAssignmentsQuery>;
  supplierId?: string;
  stagedItems: Retailer[];
  onUnstage: (retailerId: string) => void;
  page: number;
  onChangePage: (page: number) => void;
  onUnassign: (retailerId: string) => Promise<void>;
}

const ProductListRetailerTable = ({
  assignmentQuery,
  supplierId,
  stagedItems,
  onUnstage,
  page,
  onChangePage,
  onUnassign,
}: ProductListRetailerTableProps) => {
  const { assignments, itemsPerPage, isLoading, error, invalidateQuery } = assignmentQuery;
  const activeAssignments = assignments.items.filter((assignment) => isStatusActive(assignment));
  const persistedRetailerIds = activeAssignments.map((assignment) => assignment.retailerId);
  const {
    retailers,
    isLoading: retailersLoading,
    error: retailersError,
    invalidateQuery: invalidateRetailers,
  } = useGetRetailersByIdsQuery(persistedRetailerIds, supplierId);
  const retailersById = new Map(retailers.map((retailer) => [retailer.id, retailer]));
  const [unassigningId, setUnassigningId] = useState<string | null>(null);

  if (error || retailersError) {
    return (
      <ProductListErrorState
        message="Unable to load the assigned retailers."
        onRetry={() => void Promise.all([invalidateQuery(), invalidateRetailers()])}
      />
    );
  }

  const loading = isLoading || (persistedRetailerIds.length > 0 && retailersLoading);

  if (!loading && stagedItems.length === 0 && activeAssignments.length === 0) {
    return (
      <ProductListEmptyState
        title="No retailers assigned to this list yet"
        description="Search for connected retailers above to add one to this Product List."
      />
    );
  }

  const handleUnassign = async (retailerId: string) => {
    setUnassigningId(retailerId);
    try {
      await onUnassign(retailerId);
      if (activeAssignments.length === 1 && page > 1) onChangePage(page - 1);
    } finally {
      setUnassigningId(null);
    }
  };

  return (
    <div className="table-container">
      <div className="table-wrapper">
        <table className={loading ? 'loading' : ''} aria-label="Retailer assignments">
          <thead>
            <tr>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {stagedItems.map((retailer) => (
              <tr key={`staged-${retailer.id}`} className="bg-blue-50">
                <td>{retailer.name}</td>
                <td>
                  <Button
                    variant="delete"
                    size="xs"
                    onClick={() => onUnstage(retailer.id)}
                    icon={<TrashIcon className="h-5 w-5" aria-hidden="true" />}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            {activeAssignments.map((assignment) => {
              const retailer = retailersById.get(assignment.retailerId);
              return (
                <tr key={assignment.retailerId}>
                  <td>{retailer?.name ?? 'Retailer unavailable'}</td>
                  <td>
                    <Confirm
                      onConfirm={() => handleUnassign(assignment.retailerId)}
                      title="Remove retailer"
                      message={`Are you sure you want to remove "${retailer?.name ?? assignment.retailerId}" from this Product List?`}
                      confirmText="Remove"
                      danger
                    >
                      {(handleClick, confirming) => (
                        <Button
                          onClick={handleClick}
                          loading={confirming || unassigningId === assignment.retailerId}
                          variant="delete"
                          size="xs"
                          icon={<TrashIcon className="h-5 w-5" aria-hidden="true" />}
                        >
                          Remove
                        </Button>
                      )}
                    </Confirm>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading && (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading assigned retailers...</span>
          </div>
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={assignments.totalCount || 0}
          estimatedTotalCount={assignments.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default ProductListRetailerTable;
